import React, { useCallback, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import PostCompletionScreen from './PostCompletionScreen';
import { resolvePostCompletionPreviewScenario } from './postCompletionPreviewScenarios';
import getShareUrl from '../../utils/getShareUrl';

const previewShellStyle = {
  minHeight: '100vh',
  width: '100%',
  background:
    'radial-gradient(circle at 20% 20%, rgba(34,211,238,0.2), transparent 42%), #020617',
};

const previewHudStyle = {
  position: 'fixed',
  top: 12,
  left: 12,
  zIndex: 2200,
  width: 'min(820px, calc(100vw - 24px))',
  background: 'rgba(2,6,23,0.88)',
  border: '1px solid rgba(148,163,184,0.28)',
  color: '#dbeafe',
  borderRadius: 10,
  padding: '10px 12px',
  fontSize: 12,
  lineHeight: 1.4,
};

const linkStyle = {
  color: '#67e8f9',
  textDecoration: 'none',
  marginRight: 8,
  display: 'inline-block',
};

export default function PostCompletionPreviewPage() {
  const { scenario: scenarioFromPath } = useParams();
  const location = useLocation();
  const [lastAction, setLastAction] = useState('none');
  const [lastSharePayload, setLastSharePayload] = useState(null);
  const [lastShareUrl, setLastShareUrl] = useState('');
  const isScreenshotCaptureMode = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return params.get('capture') === 'screenshot';
  }, [location.search]);

  const resolved = useMemo(
    () => resolvePostCompletionPreviewScenario(scenarioFromPath),
    [scenarioFromPath],
  );
  const {
    scenario,
    invalidScenarioName,
    defaultScenarioName,
    validScenarioNames,
  } = resolved;

  const handleClose = useCallback(() => setLastAction('close'), []);
  const handlePrimaryAction = useCallback(() => setLastAction('primary'), []);
  const handleSecondaryAction = useCallback(() => setLastAction('secondary'), []);
  const handleShare = useCallback((payload = {}) => {
    const entityType = payload?.entityType === 'offering' ? 'offering' : 'task';
    const rawEntityId =
      payload?.entityId ||
      payload?.id ||
      (entityType === 'offering' ? payload?.offeringId : payload?.taskId) ||
      '';
    const entityId = String(rawEntityId || '').trim();
    const resolvedUrl = entityId ? getShareUrl(entityType, entityId) : '';
    setLastAction(`share:${entityType}:${entityId || 'unknown'}`);
    setLastSharePayload({
      ...payload,
      entityType,
      entityId,
    });
    setLastShareUrl(resolvedUrl);
  }, []);
  const serializedLastSharePayload = useMemo(() => {
    if (!lastSharePayload) return '';
    try {
      return JSON.stringify(lastSharePayload);
    } catch (err) {
      return '';
    }
  }, [lastSharePayload]);

  return (
    <div style={previewShellStyle}>
      {isScreenshotCaptureMode ? null : (
        <div style={previewHudStyle} data-testid="completion-preview-title">
          <div>
            <strong>Completion Preview</strong>
            {` | scenario: ${scenario?.name || defaultScenarioName}`}
          </div>
          {invalidScenarioName ? (
            <div
              data-testid="completion-preview-invalid"
              style={{ marginTop: 6, color: '#fde68a' }}
            >
              {`Invalid scenario "${invalidScenarioName}". Showing fallback "${scenario?.name || defaultScenarioName}". Valid scenarios: ${validScenarioNames.join(
                ', ',
              )}`}
            </div>
          ) : null}
          <div style={{ marginTop: 6 }}>
            {`Last action: ${lastAction}`}
          </div>
          {lastShareUrl ? (
            <div style={{ marginTop: 6 }}>
              {`Last share URL: ${lastShareUrl}`}
            </div>
          ) : null}
          <div style={{ marginTop: 6 }}>
            {validScenarioNames.map((name) => (
              <Link
                key={name}
                to={`/_ui/localops/completion/${encodeURIComponent(name)}`}
                style={linkStyle}
              >
                {name}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div
        data-testid="completion-preview-meta"
        data-scenarios={JSON.stringify(validScenarioNames)}
        data-default={defaultScenarioName}
        style={{ display: 'none' }}
      />
      <div
        data-testid="completion-preview-ready"
        data-ready="1"
        data-scenario={scenario?.name || defaultScenarioName}
        style={{ display: 'none' }}
      />
      <div
        data-testid="completion-preview-share"
        data-last-action={lastAction}
        data-share-url={lastShareUrl}
        data-share-payload={serializedLastSharePayload}
        data-has-share-url={lastShareUrl ? '1' : '0'}
        style={{ display: 'none' }}
      />

      <PostCompletionScreen
        open
        context={scenario?.context}
        completionContext={scenario?.context}
        playerBalance={scenario?.playerBalance}
        onPrimaryAction={handlePrimaryAction}
        onSecondaryAction={handleSecondaryAction}
        onShare={handleShare}
        onClose={handleClose}
      />
    </div>
  );
}

